import featureFlags from 'util/featureFlags';
import { getFavoritesLink } from 'util/getFavoritesLink';
import { isUserPostWedding } from '@zola-helpers/client/dist/es/util/dateUtils';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/types/constants/navConstants';
import getRegistryShopLinks from '../nav/data/registryShopLinks';
import getWeddingBoutiqueLinks from '../nav/data/weddingBoutiqueLinks';
import registryAccountLinks from '../nav/data/registryAccountLinks';

export type MobileNavItem = {
  title: string;
  path?: string;
  text?: string;
  children?: MobileNavItem[];
  color?: string;
  background?: string;
  type?: string;
  showCaret?: boolean;
  iconClassNames?: string;
  icon?: string;
};

class MobileNavData {
  userContext: UserContext;

  isUserLoggedIn: boolean;

  hasWeddingAccount: boolean | undefined;

  constructor(userContext: UserContext = {}) {
    this.userContext = userContext;
    this.isUserLoggedIn = !userContext.is_guest;
    this.hasWeddingAccount = userContext.has_wedding_account;
  }

  getInvitesLinks(hasInvitationAccount?: boolean): MobileNavItem[] {
    const invitesNavLinks: MobileNavItem[] = [
      {
        title: 'Matching Suites',
        path: '/wedding-planning/suites/shop',
      },
      { title: 'Save The Dates', path: '/wedding-planning/save-the-date/shop' },
      {
        title: 'Digital Save The Dates',
        path: '/wedding-planning/digital/save-the-date/shop',
      },
      { title: 'Invitations', path: '/wedding-planning/invitations/shop' },
      { title: 'Enclosure Cards', path: '/wedding-planning/enclosures/shop' },
      { title: 'Menus', path: '/wedding-planning/menus/shop' },
      { title: 'Programs', path: '/wedding-planning/programs/shop' },
      {
        title: 'Place Cards',
        path: '/wedding-planning/place-cards/shop',
      },
      { title: 'Thank You Cards', path: '/wedding-planning/thank-you-cards/shop' },
    ];

    if (hasInvitationAccount) {
      invitesNavLinks.unshift({
        title: 'Your Favorites',
        path: '/favorites/paper',
      });

      const dstdDropdown = {
        showCaret: true,
        children: [
          {
            path: '/wedding-planning/projects/drafts',
            title: 'Your Drafts',
          },
          {
            path: '/wedding-planning/projects/finished',
            title: 'Your Orders',
          },
        ],
      };

      invitesNavLinks.unshift({
        title: 'Your Orders + Drafts',
        path: '/wedding-planning/projects/drafts',
        ...dstdDropdown,
      });
    }
    return invitesNavLinks;
  }

  getAlbumsData(hasOnboardedAlbums?: boolean): MobileNavItem {
    const children: MobileNavItem[] = [
      { title: 'Shop Albums', path: '/wedding-albums/shop' },
      {
        title: 'Classic Album',
        path: '/wedding-albums/select?album_type=classic',
      },
      {
        title: 'Premier Album',
        path: '/wedding-albums/select?album_type=premier',
      },
      {
        title: 'Deluxe Album',
        path: '/wedding-albums/select?album_type=deluxe',
      },
    ];

    if (hasOnboardedAlbums) {
      children.unshift({
        title: 'Your Drafts and Orders',
        path: '/wedding-albums/drafts',
      });
    }

    return {
      title: 'Albums',
      type: 'toggle',
      children,
    };
  }

  getWebsiteData(hasWeddingAccount?: boolean): MobileNavItem {
    let children: MobileNavItem[] = [
      { title: 'Explore Website', path: '/wedding-planning/website' },
      { title: 'View Designs', path: '/wedding-planning/website/designs' },
    ];
    // TODO: the spec requests this be determined based on hasWebsite. Confirm wedding_account is good enough.
    if (hasWeddingAccount) {
      children = [
        { title: 'Your Website', path: '/wedding/manage/website/manage' },
        { title: 'Edit Pages', path: '/wedding/manage/website/pages/home' },
        { title: 'Change Design', path: '/wedding/manage/website/designs' },
      ];
    }
    return { title: 'Website', type: 'toggle', children };
  }

  getGuestlistData(hasWeddingAccount?: boolean): MobileNavItem {
    if (!hasWeddingAccount) {
      return { title: 'Guest List', type: 'link', path: '/wedding-planning/guests' };
    }
    const superLink: MobileNavItem = {
      title: 'Your Contact Collector',
      path: '/wedding/manage/guests/collection-link',
    };
    const children: MobileNavItem[] = [
      { title: 'Your Guest List', path: '/wedding/manage/guests/all' },
      { title: 'Your Events', path: '/wedding/manage/events' },
      { title: 'Build Guest Lists', path: '/wedding/manage/guests/build' },
      superLink,
      { title: 'Address Envelopes', path: '/wedding/manage/guests/envelopes' },
      { title: 'Track RSVPs', path: '/wedding/manage/guests/rsvps/overview' },
      { title: 'Hotel Blocks', path: 'https://zola.hotelplanner.com/Group-Rate/?kw=guestnav' },
    ];
    // TODO: check why flag off on prod
    const gmPhase1Children: MobileNavItem[] = [
      { title: 'Manage Guest List', path: '/wedding/manage/guests/all' },
      { title: 'Invite Guests to Events', path: '/wedding/manage/guests/build' },
      superLink,
      { title: 'Address Envelopes', path: '/wedding/manage/guests/envelopes' },
      { title: 'Track RSVPs', path: '/wedding/manage/guests/rsvps/overview' },
      { title: 'Your Events', path: '/wedding/manage/events' },
      { title: 'Your Seating Chart', path: '/wedding/manage/seating-chart' },
      { title: 'Hotel Blocks', path: 'https://zola.hotelplanner.com/Group-Rate/?kw=guestnav' },
    ];

    return featureFlags.get('gmPhase1')
      ? { title: 'Guest List', type: 'toggle', children: gmPhase1Children }
      : { title: 'Guest List', type: 'toggle', children };
  }

  getRegistryPreAuthLinks(): MobileNavItem[] {
    return [
      { title: 'Build Your Registry', path: '/onboard/new' },
      { title: 'Explore Registry', path: '/wedding-registry' },
      { title: 'Registry Benefits', path: '/wedding-registry/benefits' },
      { title: 'How It Works', path: '/wedding-registry/how-it-works' },
    ];
  }

  getVendorLinks(isLoggedIn?: boolean): MobileNavItem[] {
    if (isLoggedIn) {
      return [
        {
          title: 'Your Vendor Inquiries',
          path: '/wedding-vendors/inquiries',
        },
        { title: 'Your Booked Vendors', path: '/wedding-vendors/inquiries/booked' },
        {
          title: 'Your Favorites',
          path: '/favorites/home',
        },
        { title: 'Real Wedding Inspiration', path: '/inspiration' },
        {
          title: 'Explore Vendors',
          path: '/wedding-vendors',
        },
      ];
    }
    return [
      { title: 'Real Wedding Inspiration', path: '/inspiration' },
      {
        title: 'Explore Vendors',
        path: '/wedding-vendors',
      },
      { title: 'Become A Vendor', path: 'https://www.zola.com/for-vendors/' },
    ];
  }

  getAdviceLinks(): MobileNavItem[] {
    return [
      { title: 'Expert Advice', path: '/expert-advice' },
      { title: 'Wedding Checklist', path: '/wedding/manage/wedding-checklist' },
    ];
  }

  getAccountLinks(userContext: UserContext): MobileNavItem[] {
    const accountLinks: MobileNavItem[] = [
      { title: 'Your Information', path: '/account/settings/info' },
    ];

    if (userContext.has_completed_onboarding) {
      accountLinks.push({
        title: 'Wedding Details',
        path: '/account/settings/wedding',
      });
    }

    accountLinks.push(
      {
        title: "Orders You've Placed",
        path: '/account/settings/orders',
      },
      {
        title: 'Zola Store Credit',
        path: '/account/settings/credits',
      },
      {
        title: 'Ratings and Reviews',
        text: 'Ratings and Reviews',
        path: '/account/settings/reviews',
      },
      {
        title: 'Email Preferences',
        path: '/account/settings/notifications',
      },
      {
        title: 'Change Password',
        path: '/account/settings/password-security',
      }
    );

    // only if registry or wedding
    if (userContext.has_registry || userContext.has_wedding_account) {
      accountLinks.push({
        title: 'Invite Partner',
        path: '/account/settings/invitepartner',
      });
    }

    if (userContext.has_registry) {
      accountLinks.push({
        title: 'Registry Shipping Address',
        path: '/account/settings/shipping',
      });
    }

    if (userContext.has_registry || userContext.has_wedding_account) {
      accountLinks.push(
        {
          title: 'URLs',
          path: '/account/settings/urls',
        },
        {
          title: 'Privacy',
          path: '/account/settings/privacy',
        }
      );
    }

    return accountLinks;
  }

  getHelpLinks(): MobileNavItem[] {
    return [
      { title: 'FAQs', path: '/faq' },
      { title: 'Contact Us', path: '/contact-us' },
    ];
  }

  getPreAuthLinks(isShopHolidaySeason: boolean): MobileNavItem[] {
    return [
      { title: 'Find a Couple', type: 'link', path: '/search/wedding-registry' },
      { title: 'Registry', type: 'toggle', children: this.getRegistryPreAuthLinks() },
      {
        title: 'Browse Gifts',
        type: 'toggle',
        children: getRegistryShopLinks(isShopHolidaySeason),
      },
      { title: 'Invites + Paper', type: 'toggle', children: this.getInvitesLinks() },
      this.getWebsiteData(),
      { title: 'Guest List', type: 'link', path: '/wedding-planning/guests' },
      this.getAlbumsData(),
      { title: 'Boutique', type: 'toggle', children: getWeddingBoutiqueLinks() },
      { title: 'Vendors', type: 'toggle', children: this.getVendorLinks() },
      { title: 'Advice', type: 'toggle', children: this.getAdviceLinks() },
      { title: 'Help', type: 'toggle', children: this.getHelpLinks(), background: 'grey' },
    ];
  }

  getHomeStoreLinks(): MobileNavItem[] {
    return [
      { title: 'New Arrivals', type: 'link', path: '/shop/collection/new-arrivals' },
      {
        title: 'Collections',
        type: 'link',
        path: '/shop/gift-collections/style-collections',
      },
      { title: 'Brands', type: 'link', path: '/shop/brands' },
      { title: 'Kitchen', type: 'link', path: '/shop/category/kitchen' },
      { title: 'Tabletop', type: 'link', path: '/shop/category/dining' },
      { title: 'Bed & Bath', type: 'link', path: '/shop/category/bedding-and-bath' },
      { title: 'Furniture', type: 'link', path: '/shop/category/furniture' },
      { title: 'Home', type: 'link', path: '/shop/category/home' },
      { title: 'Weekend', type: 'link', path: '/shop/category/weekend' },
      { title: 'Sale', type: 'link', path: '/shop/sale', color: 'red' },
    ];
  }

  getPreAuthHomeStoreLinks(): MobileNavItem[] {
    const links = this.getHomeStoreLinks();
    links.push({
      title: 'Registry',
      type: 'toggle',
      background: 'grey',
      children: [
        { title: 'Create Registry', path: '/onboard/new' },
        { title: 'Registry Benefits', path: '/wedding-registry/benefits' },
        { title: 'How It Works', path: '/wedding-registry/how-it-works' },
        { title: 'Find A Couple ', path: '/search/wedding-registry' },
      ],
    });
    links.push({
      title: 'Help',
      type: 'toggle',
      children: this.getHelpLinks(),
      background: 'grey',
    });
    return links;
  }

  getPostAuthHomeStoreLinks(userContext: UserContext): MobileNavItem[] {
    const links = this.getHomeStoreLinks();
    if (userContext.has_registry) {
      const shopLink = isUserPostWedding(userContext)
        ? { title: 'Shop', path: '/registry/shop' }
        : { title: 'Add Gifts', path: '/registry/add-gifts' };
      links.push({
        title: 'Registry',
        type: 'toggle',
        background: 'grey',
        children: [
          { title: 'Manage Registry', path: '/my-registry' },
          shopLink,
          { title: 'Gift Tracker', path: '/registry/gift-tracker/your-gifts' },
          { title: 'Find A Couple ', path: '/search/wedding-registry' },
        ],
      });
    } else {
      links.push({
        title: 'Registry',
        type: 'toggle',
        background: 'grey',
        children: [
          { title: 'Create Registry', path: '/onboard/new' },
          { title: 'Registry Benefits', path: '/wedding-registry/benefits' },
          { title: 'How It Works', path: '/wedding-registry/how-it-works' },
          { title: 'Find A Couple ', path: '/search/wedding-registry' },
        ],
      });
    }
    links.push({
      title: 'Account',
      type: 'toggle',
      children: this.getAccountLinks(this.userContext),
      background: 'grey',
    });
    links.push({
      title: 'Help',
      type: 'toggle',
      children: this.getHelpLinks(),
      background: 'grey',
    });
    return links;
  }

  getPostAuthLinks(
    primaryLinkId: PrimaryNavIdentifier | '',
    isShopHolidaySeason: boolean
  ): MobileNavItem[] {
    const {
      has_wedding_account: hasWeddingAccount,
      has_invitation_account: hasInvitationAccount,
      is_guest: isGuest,
    } = this.userContext;

    // TODO: revisit once albums field re-thought
    const hasOnboardedAlbums =
      hasInvitationAccount && this.userContext?.invitation_account?.photobooks_onboarding_completed;

    const children = [
      { title: 'Registry', type: 'toggle', children: registryAccountLinks(this.userContext) },
      { title: 'Add Gifts', type: 'toggle', children: getRegistryShopLinks(isShopHolidaySeason) },
      {
        title: 'Invites + Paper',
        type: 'toggle',
        children: this.getInvitesLinks(hasInvitationAccount),
      },
      this.getWebsiteData(hasWeddingAccount),
      this.getGuestlistData(hasWeddingAccount),
      this.getAlbumsData(hasOnboardedAlbums),
      { title: 'Boutique', type: 'toggle', children: getWeddingBoutiqueLinks() },
      {
        title: 'Vendors',
        type: 'toggle',
        children: this.getVendorLinks(!isGuest),
      },
      { title: 'Advice', type: 'toggle', children: this.getAdviceLinks() },
      {
        title: 'Account',
        type: 'toggle',
        children: this.getAccountLinks(this.userContext),
        background: 'grey',
      },
    ];
    children.push({
      title: 'Your Wedding',
      path: '/wedding/dashboard',
      type: 'link',
    });

    if (!isGuest) {
      // only for post-auth, always show favorites icon
      children.push({
        title: 'Favorites',
        path: getFavoritesLink(primaryLinkId),
        type: 'link',
        iconClassNames: 'zola-ui-icon-heart',
        background: 'grey',
      });
    }
    children.push({
      title: 'Help',
      type: 'toggle',
      children: this.getHelpLinks(),
      background: 'grey',
    });
    return children;
  }

  getRegistryShopLinks(isShopHolidaySeason: boolean): MobileNavItem[] {
    return getRegistryShopLinks(isShopHolidaySeason);
  }
}

export default MobileNavData;
